import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Observable, Subject, Subscription, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  TaskRequest,
  TaskUpdateRequest,
  TaskResponse,
  TaskTypeResponse,
  TaskStatusResponse,
  TaskPriorityResponse,
  TaskCategoryResponse,
  TaskContactResponse,
  TaskFollowerResponse,
  TaskDuplicateCandidate,
  TaskDuplicateCandidatesParams,
  TaskDependencyResponse,
} from '../models/task.model';
import { TaskApiService } from '../services/task-api.service';
import { TaskCatalogApiService } from '../services/task-catalog-api.service';
import { TaskSubResourcesApiService } from '../services/task-sub-resources-api.service';
import { TaskErrorService } from '../services/task-error.service';
import { TaskDrawerService } from '../services/task-drawer.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { ContactoDrawerService } from '../../contactos/services/contacto-drawer.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { EmpresaDrawerService } from '../../empresas/services/empresa-drawer.service';
import { ContactCompanyApiService } from '../../empresas/services/contact-company-api.service';
import { ContactCompanyResponse } from '../../empresas/models/empresa.model';
import { DealApiService } from '../../deals/services/deal-api.service';
import { DealDrawerService } from '../../deals/services/deal-drawer.service';
import { DealContactApiService } from '../../deals/services/deal-contact-api.service';
import { LeadApiService } from '../../leads/services/lead-api.service';
import { LeadDrawerService } from '../../leads/services/lead-drawer.service';
import {
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../shared/services/user-directory-api.service';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import {
  EntityPickerComponent,
  PickerOption,
  PickerSearchPage,
} from '../../../shared/components/form/entity-picker/entity-picker.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

interface ChipItem {
  id: number;
  label: string;
  sublabel?: string;
}

interface DueQuickPreset {
  key: string;
  label: string;
  hint: string;
  apply: () => Date;
}

@Component({
  selector: 'app-tareas-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    EntityPickerComponent,
  ],
  templateUrl: './tareas-form.component.html',
})
export class TareasFormComponent implements OnInit, OnDestroy {
  // ===== Modo dual =====
  @Input() drawerMode = false;
  @Output() saved = new EventEmitter<TaskResponse>();
  @Output() cancelled = new EventEmitter<void>();

  // ===== Prefills cross-módulo =====
  @Input() prefilledContactId?: number;
  @Input() prefilledContactName?: string;
  @Input() prefilledCompanyId?: number;
  @Input() prefilledCompanyName?: string;
  @Input() prefilledDealId?: number;
  @Input() prefilledDealName?: string;
  @Input() prefilledLeadId?: number;
  @Input() prefilledLeadName?: string;
  @Input() prefilledParentTaskId?: number;
  @Input() prefilledParentTaskTitle?: string;
  @Input() prefilledDueDate?: string;
  @Input() prefilledTitle?: string;
  @Input() prefilledTypeId?: number;

  form!: FormGroup;
  isEditing = false;
  loading = false;
  error: string | null = null;
  taskId: number | null = null;

  // Límite inferior para fechas (formato YYYY-MM-DDTHH:mm)
  minDatetimeLocal = '';

  // Catálogos
  types: TaskTypeResponse[] = [];
  statuses: TaskStatusResponse[] = [];
  priorities: TaskPriorityResponse[] = [];
  categories: TaskCategoryResponse[] = [];

  // INC 8 — usuarios asignables (responsable de la tarea). Distinto del creador (lo setea el backend desde el JWT).

  // Selecciones de typeaheads single (display)
  ownerSelected: ChipItem | null = null;
  companySelected: ChipItem | null = null;
  dealSelected: ChipItem | null = null;
  leadSelected: ChipItem | null = null;
  parentTaskSelected: ChipItem | null = null;

  // Auto-fill flash flags (se cambian a true para disparar animación verde y vuelven a false)
  companyAutoFillFlash = false;
  dealAutoFillFlash = false;
  leadAutoFillFlash = false;

  // Modal cuando un contacto pertenece a varias empresas
  showCompanyChoiceModal = false;
  contactCompaniesForModal: ContactCompanyResponse[] = [];

  // Multi: contactos
  existingContacts: TaskContactResponse[] = [];
  selectedContacts: ChipItem[] = [];
  pendingAddContacts: number[] = [];
  pendingRemoveContacts: number[] = [];

  // Multi: followers
  existingFollowers: TaskFollowerResponse[] = [];
  selectedFollowers: ChipItem[] = [];
  pendingAddFollowers: number[] = [];
  pendingRemoveFollowers: number[] = [];

  isFollowingSelf = false;

  // INC 13 — Dependencias (tareas bloqueantes) gestionadas desde el form.
  // EDIT: alta/baja directa contra el CRUD (con toasts). CREATE: se acumulan en memoria
  // y se crean tras guardar la tarea (cuando ya hay id).
  dependencies: TaskDependencyResponse[] = []; // existentes (EDIT)
  pendingBlockerChips: ChipItem[] = []; // seleccionadas en CREATE (en memoria)
  newBlockedBy: ChipItem | null = null; // selección actual del picker
  addingDependency = false;

  // Duración estimada: UI en días + horas + minutos, backend en minutos
  durationDays: number | null = null;
  durationHours: number | null = null;
  durationMinutes: number | null = null;

  // Dropdown abierto en los selectores de tipo/estado/prioridad/categoría
  openDropdown: 'type' | 'status' | 'priority' | 'category' | null = null;

  readonly DUE_PRESETS: DueQuickPreset[] = [
    {
      key: 'today',
      label: 'Hoy',
      hint: 'Hoy 9 AM',
      apply: () => this.atHour(0, 9),
    },
    {
      key: 'tomorrow',
      label: 'Mañana',
      hint: 'Mañana 9 AM',
      apply: () => this.atHour(1, 9),
    },
    {
      key: 'in3',
      label: 'En 3 días',
      hint: '+3 días 9 AM',
      apply: () => this.atHour(3, 9),
    },
    {
      key: 'nextWeek',
      label: 'Próxima semana',
      hint: 'Lunes 9 AM',
      apply: () => this.nextMondayAt(9),
    },
  ];

  showAdvanced = false;

  // --- Alerta preventiva de tareas duplicadas por título (Incidente 22) ---
  duplicateCandidates: TaskDuplicateCandidate[] = [];
  duplicateDismissed = false;
  private readonly MAX_DUPLICATES_VISIBLE = 5;
  private duplicateCheckSub?: Subscription;

  // Search functions enlazadas (con paginación: term + page → PickerSearchPage)
  searchCompany = (term: string, page: number) =>
    this.searchCompaniesFn(term, page);
  searchDeal = (term: string, page: number) => this.searchDealsFn(term, page);
  searchLead = (term: string, page: number) => this.searchLeadsFn(term, page);
  searchUser = (term: string, page: number) => this.searchUsersFn(term, page);
  searchContact = (term: string, page: number) =>
    this.searchContactsFn(term, page);
  searchParentTask = (term: string, page: number) =>
    this.searchParentTaskFn(term, page);
  // INC 8 — picker del responsable: filtra en memoria la lista de usuarios asignables (muestra nombre + rol).
  searchAssignableUser = (term: string, page: number) =>
    this.searchAssignableUsersFn(term, page);
  // INC 13 — picker de tareas bloqueantes (excluye la tarea actual y las ya seleccionadas).
  searchBlocker = (term: string, page: number) =>
    this.searchBlockerFn(term, page);

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private taskApi: TaskApiService,
    private subApi: TaskSubResourcesApiService,
    private taskErrorService: TaskErrorService,
    private catalogApi: TaskCatalogApiService,
    private taskDrawerService: TaskDrawerService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private contactoDrawerService: ContactoDrawerService,
    private empresaApi: EmpresaApiService,
    private empresaDrawerService: EmpresaDrawerService,
    private contactCompanyApi: ContactCompanyApiService,
    private dealApi: DealApiService,
    private dealDrawerService: DealDrawerService,
    private dealContactApi: DealContactApiService,
    private leadApi: LeadApiService,
    private leadDrawerService: LeadDrawerService,
    private userDirectory: UserDirectoryApiService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.updateMinDate();
    this.cargarCatalogos();
    this.applyOwnerDefault();
    this.subscribeToDrawerEvents();

    if (!this.drawerMode) {
      const idParam =
        this.route.snapshot.queryParamMap.get('id') ||
        this.route.snapshot.paramMap.get('id');
      if (idParam) {
        this.taskId = Number(idParam);
        this.isEditing = true;
        this.cargarTarea(this.taskId);
      }
    }

    this.applyPrefills();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ====================================================================
  // Suscripciones a drawers: cuando otro form crea una entidad, la auto-elegimos
  // ====================================================================

  private subscribeToDrawerEvents(): void {
    this.contactoDrawerService.contactoSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((contact) => {
        if (!contact?.id) return;
        const chip: ChipItem = {
          id: contact.id,
          label: this.formatUserName(contact.fullname, contact.lastname),
          sublabel: contact.mail,
        };
        // Si no está ya en la lista, lo agregamos como contacto
        if (!this.selectedContacts.some((c) => c.id === chip.id)) {
          this.onAddContact(chip);
        }
      });

    this.empresaDrawerService.empresaSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((empresa) => {
        if (!empresa?.id) return;
        // Solo auto-seleccionamos si no había empresa elegida
        if (!this.companySelected) {
          this.onCompanyChange({
            id: empresa.id,
            label: empresa.commercialName,
          });
          this.companyAutoFillFlash = !this.companyAutoFillFlash; // toggle para forzar setter
          setTimeout(
            () => (this.companyAutoFillFlash = !this.companyAutoFillFlash),
          );
        }
      });

    this.dealDrawerService.dealSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((deal) => {
        if (!deal?.id) return;
        if (!this.dealSelected) {
          this.onDealChange({
            id: deal.id,
            label: deal.name,
            sublabel: deal.dealCycleName,
          });
        }
      });

    this.leadDrawerService.leadSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lead) => {
        if (!lead?.id) return;
        if (!this.leadSelected) {
          this.onLeadChange({ id: lead.id, label: lead.name });
        }
      });

    this.taskDrawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((task) => {
        // Si el usuario crea una tarea desde aquí (vía + Nuevo) y todavía no eligió padre, la usamos
        if (task?.id && !this.parentTaskSelected && task.id !== this.taskId) {
          this.onParentTaskChange({ id: task.id, label: task.title });
        }
      });
  }

  // ===== API pública para drawer wrapper =====

  reset(): void {
    this.updateMinDate();
    // Al reutilizar el drawer para crear, no debe quedar la fecha de la tarea anterior
    // eximida de la validación.
    this.fechaVencimientoOriginal = null;
    this.isEditing = false;
    this.taskId = null;
    this.error = null;
    this.showAdvanced = false;
    this.ownerSelected = null;
    this.companySelected = null;
    this.dealSelected = null;
    this.leadSelected = null;
    this.parentTaskSelected = null;
    this.companyAutoFillFlash = false;
    this.dealAutoFillFlash = false;
    this.leadAutoFillFlash = false;
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
    this.existingContacts = [];
    this.selectedContacts = [];
    this.pendingAddContacts = [];
    this.pendingRemoveContacts = [];
    this.existingFollowers = [];
    this.selectedFollowers = [];
    this.pendingAddFollowers = [];
    this.pendingRemoveFollowers = [];
    this.isFollowingSelf = false;
    this.dependencies = [];
    this.pendingBlockerChips = [];
    this.newBlockedBy = null;
    this.addingDependency = false;
    this.duplicateCandidates = [];
    this.duplicateDismissed = false;
    this.initForm();
    this.applyOwnerDefault();
    this.applyPrefills();
  }

  cargarTarea(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.taskId = id;

    this.taskApi
      .getTaskById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (t) => {
          this.fechaVencimientoOriginal = this.toLocalInput(t.datetimeDue) || null;
          this.form.patchValue({
            title: t.title,
            description: t.description ?? '',
            idTaskType: t.type?.id ?? null,
            idTaskStatus: t.status?.id ?? null,
            idTaskPriority: t.priority?.id ?? null,
            idTaskCategory: t.category?.id ?? null,
            datetimeDue: this.toLocalInput(t.datetimeDue),
            estimatedDurationMin: t.estimatedDurationMin ?? null,
            intIdUserOwner: t.intIdUserOwner ?? null,
            intIdCompany: t.intIdCompany ?? null,
            intIdDeal: t.intIdDeal ?? null,
            intIdLead: t.intIdLead ?? null,
            idParentTask: t.idParentTask ?? null,
            autoCompleteWhenSubtasksDone: !!t.autoCompleteWhenSubtasksDone,
          });
          this.setDurationFromMinutes(t.estimatedDurationMin ?? null);
          this.loadSingleSelectionLabels(t);
          this.loadSubResources(id);
          this.loading = false;
        },
        error: () => {
          this.error = 'No se pudo cargar la tarea.';
          this.loading = false;
        },
      });
  }

  private loadSingleSelectionLabels(t: TaskResponse): void {
    const businessId = this.businessId();
    if (t.intIdUserOwner) {
      // Modo `ids` del directorio: devuelve también a los usuarios dados de baja, así el
      // responsable de una tarea antigua se ve con su nombre y marcado, no como un hueco.
      this.userDirectory
        .fetchUsersByIds([t.intIdUserOwner])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (list) => {
            const u = list[0];
            this.ownerSelected = u
              ? {
                  id: u.id,
                  label: `${directoryUserLabel(u)}${u.isActive === false ? ' (inactivo)' : ''}`,
                  sublabel: u.mail,
                }
              : {
                  id: t.intIdUserOwner!,
                  label: `Usuario #${t.intIdUserOwner} (no disponible)`,
                };
          },
          error: () => {},
        });
    }
    if (t.intIdCompany) {
      this.empresaApi
        .getCompanyById(t.intIdCompany)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (c) =>
            (this.companySelected = { id: c.id!, label: c.commercialName }),
        });
    }
    if (t.intIdDeal) {
      this.dealApi
        .getDealById(t.intIdDeal, businessId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (d) =>
            (this.dealSelected = {
              id: d.id!,
              label: d.name,
              sublabel: d.dealCycleName,
            }),
        });
    }
    if (t.intIdLead) {
      this.leadApi
        .getLeadById(t.intIdLead, businessId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (l) => (this.leadSelected = { id: l.id!, label: l.name }),
        });
    }
    if (t.idParentTask) {
      this.taskApi
        .getTaskById(t.idParentTask)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (p) =>
            (this.parentTaskSelected = {
              id: p.id,
              label: p.title,
              sublabel: p.status?.label,
            }),
        });
    }
  }

  private loadSubResources(taskId: number): void {
    this.subApi
      .listContacts(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.existingContacts = list || [];
          this.selectedContacts = (list || []).map((c) => ({
            id: c.contactId,
            label: this.formatUserName(c.contactFullname, c.contactLastname),
            sublabel: c.contactMail,
          }));
        },
      });
    this.subApi
      .listFollowers(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.existingFollowers = list || [];
          this.selectedFollowers = (list || []).map((f) => ({
            id: f.userId,
            label: this.formatUserName(f.userFullname, f.userLastname),
            sublabel: f.userMail,
          }));
          const meId = this.authService.getCurrentUser()?.id;
          this.isFollowingSelf =
            !!meId && this.selectedFollowers.some((f) => f.id === meId);
        },
      });
    // INC 13 — dependencias existentes
    this.loadDependencies(taskId);
  }

  // ===== Init / catálogos =====

  /**
   * Actualiza el string de fecha mínima al momento actual.
   */
  private updateMinDate(): void {
    this.minDatetimeLocal = this.toLocalInputFromDate(new Date());
  }

  /**
   * Fecha de vencimiento tal como vino del backend al abrir la tarea. Sirve para NO invalidar
   * una fecha que el usuario no ha tocado (ver `futureDateValidator`). `null` al crear.
   */
  private fechaVencimientoOriginal: string | null = null;

  /**
   * Validador que asegura que la fecha no sea anterior al momento de la validación.
   *
   * OJO con la fecha que YA estaba guardada: una tarea vencida tiene, por definición, la fecha
   * en el pasado. Validándola sin más, el formulario de CUALQUIER tarea vencida quedaba
   * `invalid`, y como el botón está atado a `[disabled]="form.invalid || loading"`, «Actualizar
   * Tarea» salía deshabilitado. Encima el mensaje de error no se veía —`hasError` exige
   * `dirty || touched` y el control se rellena con `patchValue`, que no ensucia—, así que el
   * botón no respondía y no había NADA en pantalla que lo explicara. Justo las tareas vencidas
   * son las que uno vuelve a abrir para cambiarles estado o prioridad.
   *
   * Por eso solo se rechaza una fecha pasada NUEVA: si el valor sigue siendo el que se cargó,
   * pasa. En cuanto el usuario la cambia a otra fecha pasada, vuelve a fallar como antes.
   */
  private futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      if (this.fechaVencimientoOriginal !== null && control.value === this.fechaVencimientoOriginal) {
        return null;
      }
      const val = new Date(control.value);
      const now = new Date();
      // Comparamos sin segundos para evitar falsos negativos por el tiempo de carga del form
      val.setSeconds(0, 0);
      now.setSeconds(0, 0);
      return val < now ? { pastDate: true } : null;
    };
  }

  private initForm(): void {
    const user = this.authService.getCurrentUser();
    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(500)]],
      // La columna `description` es TEXT ilimitada en BD; sin maxLength para no
      // deshabilitar "Crear tarea" silenciosamente con descripciones largas (Incidente 21).
      description: ['', Validators.maxLength(5000)],
      idTaskType: [null],
      idTaskStatus: [null, Validators.required],
      idTaskPriority: [null],
      idTaskCategory: [null],
      datetimeDue: ['', [this.futureDateValidator()]],
      estimatedDurationMin: [null],
      intIdUserOwner: [user?.id ?? null],
      intIdCompany: [null],
      intIdDeal: [null],
      intIdLead: [null],
      idParentTask: [null],
      autoCompleteWhenSubtasksDone: [false],
    });
    this.setupDuplicateCheck();
  }

  // ─── Alerta preventiva de tareas duplicadas (Incidente 22) ───────────────
  // Best-effort: nunca bloquea el guardado; errores se ignoran en silencio.
  // Replica el patrón de empresas (setupDuplicateCheck / getDuplicateCandidates).
  // Se re-suscribe cada vez que initForm() recrea el FormGroup (reset del drawer).
  private setupDuplicateCheck(): void {
    this.duplicateCheckSub?.unsubscribe();
    this.duplicateCheckSub = this.form.valueChanges
      .pipe(
        debounceTime(400),
        map((v) => ({
          title: ((v.title as string) || '').trim(),
          intIdCompany: (v.intIdCompany as number | null) ?? null,
          intIdDeal: (v.intIdDeal as number | null) ?? null,
          intIdLead: (v.intIdLead as number | null) ?? null,
        })),
        distinctUntilChanged(
          (a, b) =>
            a.title === b.title &&
            a.intIdCompany === b.intIdCompany &&
            a.intIdDeal === b.intIdDeal &&
            a.intIdLead === b.intIdLead,
        ),
        switchMap((criteria) => {
          // El título cuenta como criterio solo con 3+ caracteres
          if (criteria.title.length < 3) {
            this.duplicateCandidates = [];
            return of(null);
          }

          const params: TaskDuplicateCandidatesParams = {
            title: criteria.title,
          };
          if (criteria.intIdCompany != null)
            params.intIdCompany = criteria.intIdCompany;
          if (criteria.intIdDeal != null) params.intIdDeal = criteria.intIdDeal;
          if (criteria.intIdLead != null) params.intIdLead = criteria.intIdLead;
          if (this.isEditing && this.taskId != null)
            params.excludeId = this.taskId;

          return this.taskApi.getDuplicateCandidates(params).pipe(
            catchError(() => of(null)), // silencio: la alerta es best-effort
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((candidates) => {
        if (candidates === null) return; // error o sin criterios: no cambiar nada más
        const prevIds = this.duplicateCandidates.map((c) => c.id).join(',');
        const newIds = candidates.map((c) => c.id).join(',');
        this.duplicateCandidates = candidates;
        // Si el conjunto de candidatos cambió, volver a mostrar el panel aunque se haya cerrado
        if (prevIds !== newIds) {
          this.duplicateDismissed = false;
        }
      });
  }

  get showDuplicateWarning(): boolean {
    return this.duplicateCandidates.length > 0 && !this.duplicateDismissed;
  }

  get visibleDuplicateCandidates(): TaskDuplicateCandidate[] {
    return this.duplicateCandidates.slice(0, this.MAX_DUPLICATES_VISIBLE);
  }

  get duplicateExtraCount(): number {
    return Math.max(
      0,
      this.duplicateCandidates.length - this.MAX_DUPLICATES_VISIBLE,
    );
  }

  dismissDuplicateWarning(): void {
    this.duplicateDismissed = true;
  }

  verTareaDuplicada(id: number): void {
    // Nueva pestaña: preserva lo que el usuario está escribiendo en el form actual.
    window.open(`#/tareas/editar/${id}`, '_blank');
  }

  setDurationFromMinutes(total: number | null): void {
    if (total == null || isNaN(total) || total <= 0) {
      this.durationDays = null;
      this.durationHours = null;
      this.durationMinutes = null;
      return;
    }
    const MIN_PER_DAY = 24 * 60;
    const days = Math.floor(total / MIN_PER_DAY);
    const remainingAfterDays = total - days * MIN_PER_DAY;
    const hours = Math.floor(remainingAfterDays / 60);
    const mins = remainingAfterDays % 60;
    this.durationDays = days || null;
    this.durationHours = hours || null;
    this.durationMinutes = mins || null;
  }

  onDurationChange(): void {
    const d = Math.max(0, Math.floor(Number(this.durationDays ?? 0) || 0));
    const h = Math.max(0, Math.floor(Number(this.durationHours ?? 0) || 0));
    const m = Math.max(0, Math.floor(Number(this.durationMinutes ?? 0) || 0));
    const total = d * 24 * 60 + h * 60 + m;
    this.form.patchValue({ estimatedDurationMin: total > 0 ? total : null });
  }

  private applyOwnerDefault(): void {
    const user = this.authService.getCurrentUser();
    if (user?.id) {
      this.ownerSelected = {
        id: user.id,
        label: this.formatUserName(user.fullname, user.lastname),
        sublabel: user.email,
      };
    }
  }

  private cargarCatalogos(): void {
    this.catalogApi
      .getActiveTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => (this.types = data),
        error: () => (this.types = []),
      });
    this.catalogApi
      .getActiveStatuses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.statuses = [...data].sort(
            (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
          );
          if (!this.isEditing && !this.form.value.idTaskStatus) {
            const def = data.find((s) => s.isDefault) ?? data[0];
            if (def) this.form.patchValue({ idTaskStatus: def.id });
          }
        },
        error: () => (this.statuses = []),
      });
    this.catalogApi
      .getActivePriorities()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.priorities = [...data].sort(
            (a, b) => (a.level ?? 99) - (b.level ?? 99),
          );
          if (!this.isEditing && !this.form.value.idTaskPriority) {
            const med = data.find((p) => p.level === 3) ?? data[0];
            if (med) this.form.patchValue({ idTaskPriority: med.id });
          }
        },
        error: () => (this.priorities = []),
      });
    this.catalogApi
      .getActiveCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => (this.categories = data),
        error: () => (this.categories = []),
      });
    // El picker de responsable ya no precarga el directorio: busca en servidor al teclear.
  }

  private applyPrefills(): void {
    if (this.isEditing) return;

    const patch: Record<string, unknown> = {};
    if (this.prefilledTitle) patch['title'] = this.prefilledTitle;
    if (this.prefilledTypeId) patch['idTaskType'] = this.prefilledTypeId;
    if (this.prefilledDueDate)
      patch['datetimeDue'] = this.toLocalInput(this.prefilledDueDate);
    if (this.prefilledCompanyId)
      patch['intIdCompany'] = this.prefilledCompanyId;
    if (this.prefilledDealId) patch['intIdDeal'] = this.prefilledDealId;
    if (this.prefilledLeadId) patch['intIdLead'] = this.prefilledLeadId;
    if (this.prefilledParentTaskId)
      patch['idParentTask'] = this.prefilledParentTaskId;
    if (Object.keys(patch).length) this.form.patchValue(patch);

    if (this.prefilledCompanyId) {
      this.empresaApi
        .getCompanyById(this.prefilledCompanyId)
        .subscribe((company) => {
          this.companySelected = {
            id: company.id!,
            label: company.commercialName,
          };
        });
    }
    if (this.prefilledDealId) {
      this.dealSelected = {
        id: this.prefilledDealId,
        label: this.prefilledDealName ?? `Negocio #${this.prefilledDealId}`,
      };
    }
    if (this.prefilledLeadId) {
      this.leadSelected = {
        id: this.prefilledLeadId,
        label: this.prefilledLeadName ?? `Lead #${this.prefilledLeadId}`,
      };
    }
    if (this.prefilledParentTaskId) {
      this.parentTaskSelected = {
        id: this.prefilledParentTaskId,
        label:
          this.prefilledParentTaskTitle ??
          `Tarea #${this.prefilledParentTaskId}`,
      };
    }
    if (this.prefilledContactId && !this.prefilledLeadId) {
      const chip: ChipItem = {
        id: this.prefilledContactId,
        label:
          this.prefilledContactName ?? `Contacto #${this.prefilledContactId}`,
      };
      this.selectedContacts.push(chip);
      this.pendingAddContacts.push(this.prefilledContactId);
    }

    this.autoFillCompanyFromLeadIfNeeded();
    this.autoFillCompanyFromContactIfNeeded();
    this.autoFillContactFromLeadIfNeeded();
  }

  private autoFillCompanyFromLeadIfNeeded(): void {
    if (!this.prefilledLeadId || this.companySelected) return;
    this.leadApi
      .getLeadById(this.prefilledLeadId, this.businessId())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (lead) => {
          if (lead?.idCompany && !this.companySelected) {
            this.empresaApi
              .getCompanyById(lead.idCompany)
              .pipe(
                catchError(() => of(null)),
                takeUntil(this.destroy$),
              )
              .subscribe((c) => {
                if (c && !this.companySelected) {
                  this.onCompanyChange({ id: c.id!, label: c.commercialName });
                  this.flashCompanyAutoFill();
                }
              });
          }
        },
      });
  }

  private autoFillCompanyFromContactIfNeeded(): void {
    if (!this.prefilledContactId || this.companySelected) return;
    this.suggestCompanyFromContact(this.prefilledContactId);
  }

  private autoFillContactFromLeadIfNeeded(): void {
    if (!this.prefilledLeadId) return;
    this.leadApi
      .getLeadById(this.prefilledLeadId, this.businessId())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (lead) => {
          if (!lead?.idContact) return;

          const existingIndex = this.selectedContacts.findIndex(
            (c) => c.id === lead.idContact,
          );

          this.contactoApi
            .getContactById(lead.idContact)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (contact) => {
                const fullname = contact.fullname
                  ? `${contact.fullname}${contact.lastname ? ' ' + contact.lastname : ''}`.trim()
                  : null;
                const chip: ChipItem = {
                  id: lead.idContact,
                  label: fullname ?? `Contacto #${lead.idContact}`,
                  sublabel: contact.mail ?? undefined,
                };

                if (existingIndex >= 0) {
                  this.selectedContacts[existingIndex] = chip;
                } else {
                  this.selectedContacts.push(chip);
                  this.pendingAddContacts.push(lead.idContact);
                }
              },
              error: () => {
                if (existingIndex >= 0) {
                  this.selectedContacts[existingIndex].label =
                    this.prefilledContactName ?? `Contacto #${lead.idContact}`;
                }
              },
            });
        },
      });
  }

  // ===== Selectores rápidos visuales =====

  // Tipo / Estado / Prioridad / Categoría NO son controles enlazados: son desplegables propios
  // que escriben con `patchValue`, y `patchValue` NO ensucia el formulario. Sin el
  // `markAsDirty()` de cada uno, cambiar SOLO uno de estos cuatro campos dejaba el form
  // `pristine` y `guardar()` salía por su guard con «No se realizaron nuevos cambios en la
  // tarea»: el título se guardaba (es un input enlazado, ensucia solo) y estos cuatro no.
  // Es la misma trampa que ya mordió en otros formularios; el resto de setters de esta clase
  // (empresa, negocio, lead…) ya llamaban a `markAsDirty()`.
  setType(id: number | null): void {
    this.form.patchValue({
      idTaskType: this.form.value.idTaskType === id ? null : id,
    });
    this.form.markAsDirty();
    this.openDropdown = null;
  }
  setStatus(id: number): void {
    this.form.patchValue({ idTaskStatus: id });
    this.form.markAsDirty();
    this.openDropdown = null;
  }
  setPriority(id: number): void {
    this.form.patchValue({
      idTaskPriority: this.form.value.idTaskPriority === id ? null : id,
    });
    this.form.markAsDirty();
    this.openDropdown = null;
  }
  setCategory(id: number | null): void {
    this.form.patchValue({
      idTaskCategory: this.form.value.idTaskCategory === id ? null : id,
    });
    this.form.markAsDirty();
    this.openDropdown = null;
  }

  toggleDropdown(
    name: 'type' | 'status' | 'priority' | 'category',
    ev?: Event,
  ): void {
    ev?.stopPropagation();
    this.openDropdown = this.openDropdown === name ? null : name;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openDropdown = null;
  }

  selectedType(): TaskTypeResponse | null {
    return this.types.find((t) => t.id === this.form.value.idTaskType) || null;
  }
  selectedStatus(): TaskStatusResponse | null {
    return (
      this.statuses.find((s) => s.id === this.form.value.idTaskStatus) || null
    );
  }
  selectedPriority(): TaskPriorityResponse | null {
    return (
      this.priorities.find((p) => p.id === this.form.value.idTaskPriority) ||
      null
    );
  }
  selectedCategory(): TaskCategoryResponse | null {
    return (
      this.categories.find((c) => c.id === this.form.value.idTaskCategory) ||
      null
    );
  }

  applyDuePreset(p: DueQuickPreset): void {
    this.form.patchValue({ datetimeDue: this.toLocalInputFromDate(p.apply()) });
  }
  clearDue(): void {
    this.form.patchValue({ datetimeDue: '' });
  }

  // ===== Handlers de typeahead single =====

  onOwnerChange(opt: PickerOption | null): void {
    this.ownerSelected = opt;
    this.form.patchValue({ intIdUserOwner: opt?.id ?? null });
    this.form.markAsDirty();
  }
  onCompanyChange(opt: PickerOption | null): void {
    const previousId = this.companySelected?.id;
    this.setCompanySelection(opt);
    // INC 2 — al elegir EMPRESA: autocompletar deals, leads y contactos vacíos.
    // Solo disparamos la cascada cuando la empresa cambia a una nueva (evita loops y re-cargas).
    if (opt && opt.id !== previousId) this.autoFillFromCompany(opt.id);
    this.form.markAsDirty();
  }

  /** Setter de empresa sin cascada (lo usan las rutas de auto-fill para no re-disparar autoFillFromCompany). */
  private setCompanySelection(opt: PickerOption | null): void {
    this.companySelected = opt;
    this.form.patchValue({ intIdCompany: opt?.id ?? null });
    this.form.markAsDirty();
  }
  onLeadChange(opt: PickerOption | null): void {
    this.leadSelected = opt;
    this.form.patchValue({ intIdLead: opt?.id ?? null });

    // INC 2 — al elegir LEAD: autocompletar empresa, deal y contactos vacíos.
    if (opt) this.autoFillFromLead(opt.id);
    this.form.markAsDirty();
  }
  onDealChange(opt: PickerOption | null): void {
    this.dealSelected = opt;
    this.form.patchValue({ intIdDeal: opt?.id ?? null });
    // INC 2 — al elegir NEGOCIO/DEAL: autocompletar empresa, lead y contactos vacíos.
    if (opt) this.autoFillFromDeal(opt.id);
    this.form.markAsDirty();
  }
  onParentTaskChange(opt: PickerOption | null): void {
    this.parentTaskSelected = opt;
    this.form.patchValue({ idParentTask: opt?.id ?? null });
    this.form.markAsDirty();
  }

  // ====================================================================
  // INC 2 — Autocompletado best-effort de campos relacionados.
  // Reglas: solo se rellena un campo single cuando está VACÍO; el usuario
  // SIEMPRE puede sobrescribir. Si una relación no tiene datos, se deja vacío.
  // Para relaciones single con varios candidatos NO se impone uno arbitrario.
  // Errores silenciosos (nunca bloquean el guardado).
  // ====================================================================

  /** Al elegir EMPRESA: cargar sus deals, leads y contactos asociados. */
  private autoFillFromCompany(companyId: number): void {
    const biz = this.businessId();

    // Deal: solo autocompletar si hay exactamente uno (no imponer arbitrario).
    if (!this.dealSelected) {
      this.dealApi
        .searchDeals({
          businessId: biz,
          idCompany: companyId,
          page: 0,
          size: 5,
        })
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroy$),
        )
        .subscribe((res) => {
          const list = res?.content || [];
          if (!this.dealSelected && list.length === 1) {
            const d = list[0];
            this.onDealChange({
              id: d.id!,
              label: d.name,
              sublabel: d.dealCycleName || undefined,
            });
            this.flashDealAutoFill();
          }
        });
    }

    // Lead: solo autocompletar si hay exactamente uno.
    if (!this.leadSelected) {
      this.leadApi
        .searchLeads({
          businessId: biz,
          idCompany: companyId,
          page: 0,
          size: 5,
        })
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroy$),
        )
        .subscribe((res) => {
          const list = res?.content || [];
          if (!this.leadSelected && list.length === 1) {
            const l = list[0];
            this.onLeadChange({ id: l.id!, label: l.name });
            this.flashLeadAutoFill();
          }
        });
    }

    // Contactos: agregar los asociados si aún no hay ninguno seleccionado.
    if (this.selectedContacts.length === 0) {
      this.contactCompanyApi
        .getByCompany(companyId, biz)
        .pipe(
          catchError(() => of([] as ContactCompanyResponse[])),
          takeUntil(this.destroy$),
        )
        .subscribe((assocs) => {
          if (this.selectedContacts.length > 0) return;
          for (const a of assocs || []) {
            this.addContactChipSilently(
              a.idContact,
              a.contact?.fullname,
              a.contact?.lastname,
              a.contact?.mail,
            );
          }
        });
    }
  }

  /** Al elegir CONTACTO: cargar su empresa, deals y leads. */
  private autoFillFromContact(contactId: number): void {
    const biz = this.businessId();

    // Empresa: reutiliza la lógica existente (1 → auto, varias → modal de elección).
    if (!this.companySelected) {
      this.suggestCompanyFromContact(contactId);
    }

    // Deal: vía puente deal_contact. Auto solo si exactamente uno.
    if (!this.dealSelected) {
      this.dealContactApi
        .getByContact(contactId, biz)
        .pipe(
          catchError(() => of([])),
          takeUntil(this.destroy$),
        )
        .subscribe((list) => {
          if (!this.dealSelected && list && list.length === 1) {
            const dc = list[0];
            this.dealApi
              .getDealById(dc.idDeal, biz)
              .pipe(
                catchError(() => of(null)),
                takeUntil(this.destroy$),
              )
              .subscribe((d) => {
                if (d && !this.dealSelected) {
                  this.onDealChange({
                    id: d.id!,
                    label: d.name,
                    sublabel: d.dealCycleName || undefined,
                  });
                  this.flashDealAutoFill();
                }
              });
          }
        });
    }

    // Lead: filtrando por contacto. Auto solo si exactamente uno.
    if (!this.leadSelected) {
      this.leadApi
        .searchLeads({
          businessId: biz,
          idContact: contactId,
          page: 0,
          size: 5,
        })
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroy$),
        )
        .subscribe((res) => {
          const list = res?.content || [];
          if (!this.leadSelected && list.length === 1) {
            const l = list[0];
            this.onLeadChange({ id: l.id!, label: l.name });
            this.flashLeadAutoFill();
          }
        });
    }
  }

  /** Al elegir NEGOCIO/DEAL: cargar empresa, lead y contactos. */
  private autoFillFromDeal(dealId: number): void {
    const biz = this.businessId();

    this.dealApi
      .getDealById(dealId, biz)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((d) => {
        // Empresa
        if (!d) return;
        if (d.idCompany && !this.companySelected) {
          this.empresaApi
            .getCompanyById(d.idCompany)
            .pipe(
              catchError(() => of(null)),
              takeUntil(this.destroy$),
            )
            .subscribe((c) => {
              if (c && !this.companySelected) {
                this.setCompanySelection({
                  id: c.businessId,
                  label: c.commercialName || `Empresa #${d.idCompany}`,
                });
                this.flashCompanyAutoFill();
              }
            });
        }
        // Lead que originó el negocio
        if (d.idLead && !this.leadSelected) {
          this.leadApi
            .getLeadById(d.idLead, biz)
            .pipe(
              catchError(() => of(null)),
              takeUntil(this.destroy$),
            )
            .subscribe((l) => {
              if (l && !this.leadSelected) {
                this.onLeadChange({ id: l.id!, label: l.name });
                this.flashLeadAutoFill();
              }
            });
        }
      });

    // Contactos del deal (puente deal_contact). Agregar si aún no hay ninguno.
    if (this.selectedContacts.length === 0) {
      this.dealContactApi
        .getByDeal(dealId, biz)
        .pipe(
          catchError(() => of([])),
          takeUntil(this.destroy$),
        )
        .subscribe((list) => {
          if (this.selectedContacts.length > 0) return;
          for (const dc of list || []) {
            this.addContactChipSilently(
              dc.idContact,
              dc.contactFullname,
              dc.contactLastname,
              dc.contactMail,
            );
          }
        });
    }
  }

  /** Al elegir LEAD: cargar empresa, deal y contacto. */
  private autoFillFromLead(leadId: number): void {
    const biz = this.businessId();

    this.leadApi
      .getLeadById(leadId, biz)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((lead) => {
        if (!lead) return;
        // Empresa
        if (lead.idCompany && !this.companySelected) {
          this.empresaApi
            .getCompanyById(lead.idCompany)
            .pipe(
              catchError(() => of(null)),
              takeUntil(this.destroy$),
            )
            .subscribe((c) => {
              if (c && !this.companySelected) {
                this.setCompanySelection({
                  id: c.id!,
                  label: c.commercialName,
                });
                this.flashCompanyAutoFill();
              }
            });
        }
        // Contacto (1 por lead vía FK)
        if (lead.idContact && this.selectedContacts.length === 0) {
          this.addContactChipSilently(
            lead.idContact,
            lead.contactFullname,
            lead.contactLastname,
          );
        }
      });

    // Deals originados por el lead. Auto solo si exactamente uno.
    if (!this.dealSelected) {
      this.dealApi
        .searchDeals({ businessId: biz, idLead: leadId, page: 0, size: 5 })
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroy$),
        )
        .subscribe((res) => {
          const list = res?.content || [];
          if (!this.dealSelected && list.length === 1) {
            const d = list[0];
            this.onDealChange({
              id: d.id!,
              label: d.name,
              sublabel: d.dealCycleName || undefined,
            });
            this.flashDealAutoFill();
          }
        });
    }
  }

  /**
   * Agrega un contacto a la lista de seleccionados SIN re-disparar la sugerencia de empresa
   * (la cadena de auto-fill ya gestiona la empresa). Registra el pendiente de asociación en create.
   */
  private addContactChipSilently(
    contactId: number,
    fullname?: string | null,
    lastname?: string | null,
    mail?: string | null,
  ): void {
    if (this.selectedContacts.some((c) => c.id === contactId)) return;
    const chip: ChipItem = {
      id: contactId,
      label:
        this.formatUserName(fullname, lastname) === '—'
          ? `Contacto #${contactId}`
          : this.formatUserName(fullname, lastname),
      sublabel: mail || undefined,
    };
    this.selectedContacts.push(chip);

    if (this.isEditing && this.taskId) {
      this.subApi
        .addContact(this.taskId, { contactId })
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.destroy$),
        )
        .subscribe();
    } else if (!this.pendingAddContacts.includes(contactId)) {
      this.pendingAddContacts.push(contactId);
    }
  }

  /** Trigger de animación del badge "Auto-completado" en el picker de empresa. */
  private flashCompanyAutoFill(): void {
    this.companyAutoFillFlash = !this.companyAutoFillFlash;
    setTimeout(() => {
      this.companyAutoFillFlash = !this.companyAutoFillFlash;
    });
  }
  private flashDealAutoFill(): void {
    this.dealAutoFillFlash = !this.dealAutoFillFlash;
    setTimeout(() => {
      this.dealAutoFillFlash = !this.dealAutoFillFlash;
    });
  }
  private flashLeadAutoFill(): void {
    this.leadAutoFillFlash = !this.leadAutoFillFlash;
    setTimeout(() => {
      this.leadAutoFillFlash = !this.leadAutoFillFlash;
    });
  }

  // ===== Multi: contactos =====

  onAddContact(opt: PickerOption | ChipItem): void {
    if (this.selectedContacts.some((c) => c.id === opt.id)) return;
    const chip: ChipItem = {
      id: opt.id,
      label: opt.label,
      sublabel: opt.sublabel,
    };
    this.selectedContacts.push(chip);
    this.form.markAsDirty();
    if (this.isEditing && this.taskId) {
      const idx = this.pendingRemoveContacts.indexOf(opt.id);
      if (idx >= 0) {
        this.pendingRemoveContacts.splice(idx, 1);
      } else {
        this.subApi
          .addContact(this.taskId, { contactId: opt.id })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            error: () => {
              this.selectedContacts = this.selectedContacts.filter(
                (c) => c.id !== opt.id,
              );
              this.confirmDialog.alert({
                title: 'Error',
                message: 'No se pudo asociar el contacto.',
                variant: 'danger',
              });
            },
          });
      }
    } else {
      if (!this.pendingAddContacts.includes(opt.id))
        this.pendingAddContacts.push(opt.id);
    }

    // INC 2 — al elegir el primer CONTACTO: autocompletar empresa, deal y lead vacíos.
    if (this.selectedContacts.length === 1) {
      this.autoFillFromContact(opt.id);
    }
  }

  removeContact(c: ChipItem): void {
    this.selectedContacts = this.selectedContacts.filter((x) => x.id !== c.id);

    if (this.isEditing && this.taskId) {
      const wasPending = this.pendingAddContacts.includes(c.id);
      this.pendingAddContacts = this.pendingAddContacts.filter(
        (id) => id !== c.id,
      );
      const wasExisting = this.existingContacts.some(
        (x) => x.contactId === c.id,
      );
      this.form.markAsDirty();
      if (wasExisting && !wasPending) {
        this.subApi
          .removeContact(this.taskId, c.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            error: () => {
              this.selectedContacts.push(c);
              this.confirmDialog.alert({
                title: 'Error',
                message: 'No se pudo quitar el contacto.',
                variant: 'danger',
              });
            },
          });
      }
    } else {
      this.pendingAddContacts = this.pendingAddContacts.filter(
        (id) => id !== c.id,
      );
    }
  }

  /** Para un contacto, busca sus empresas. Si solo tiene una, auto-completa; si varias, abre modal. */
  private suggestCompanyFromContact(contactId: number): void {
    this.contactCompanyApi
      .getByContact(contactId, this.businessId())
      .pipe(
        catchError(() => of([] as ContactCompanyResponse[])),
        takeUntil(this.destroy$),
      )
      .subscribe((assocs) => {
        if (this.companySelected) return; // el usuario eligió empresa mientras tanto
        if (!assocs || assocs.length === 0) return;

        if (assocs.length === 1) {
          const a = assocs[0];
          this.setCompanySelection({
            id: a.idCompany,
            label: a.company?.commercialName || `Empresa #${a.idCompany}`,
          });
          this.flashCompanyAutoFill();
        } else {
          this.contactCompaniesForModal = assocs;
          this.showCompanyChoiceModal = true;
        }
      });
  }

  selectCompanyFromModal(a: ContactCompanyResponse): void {
    this.setCompanySelection({
      id: a.idCompany,
      label: a.company?.commercialName || `Empresa #${a.idCompany}`,
    });
    this.flashCompanyAutoFill();
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
  }

  skipCompanyChoiceModal(): void {
    this.showCompanyChoiceModal = false;
    this.contactCompaniesForModal = [];
  }

  // ===== Multi: followers =====

  onAddFollower(opt: PickerOption): void {
    if (this.selectedFollowers.some((f) => f.id === opt.id)) return;
    this.selectedFollowers.push({
      id: opt.id,
      label: opt.label,
      sublabel: opt.sublabel,
    });
    this.form.markAsDirty();
    if (this.isEditing && this.taskId) {
      const idx = this.pendingRemoveFollowers.indexOf(opt.id);
      if (idx >= 0) {
        this.pendingRemoveFollowers.splice(idx, 1);
        return;
      }

      this.subApi
        .addFollower(this.taskId, { userId: opt.id })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            const meId = this.authService.getCurrentUser()?.id;
            if (meId === opt.id) this.isFollowingSelf = true;
          },
          error: () => {
            this.selectedFollowers = this.selectedFollowers.filter(
              (f) => f.id !== opt.id,
            );
            this.confirmDialog.alert({
              title: 'Error',
              message: 'No se pudo agregar el follower.',
              variant: 'danger',
            });
          },
        });
    } else {
      if (!this.pendingAddFollowers.includes(opt.id))
        this.pendingAddFollowers.push(opt.id);
    }
  }

  removeFollower(f: ChipItem): void {
    this.selectedFollowers = this.selectedFollowers.filter(
      (x) => x.id !== f.id,
    );
    if (this.isEditing && this.taskId) {
      const wasPending = this.pendingAddFollowers.includes(f.id);
      this.pendingAddFollowers = this.pendingAddFollowers.filter(
        (id) => id !== f.id,
      );
      const wasExisting = this.existingFollowers.some((x) => x.userId === f.id);
      this.form.markAsDirty();
      if (wasExisting && !wasPending) {
        this.subApi
          .removeFollower(this.taskId, f.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              const meId = this.authService.getCurrentUser()?.id;
              if (meId === f.id) this.isFollowingSelf = false;
            },
            error: () => {
              this.selectedFollowers.push(f);
              this.confirmDialog.alert({
                title: 'Error',
                message: 'No se pudo quitar el follower.',
                variant: 'danger',
              });
            },
          });
      } else {
        const meId = this.authService.getCurrentUser()?.id;
        if (meId === f.id) this.isFollowingSelf = false;
      }
    } else {
      this.pendingAddFollowers = this.pendingAddFollowers.filter(
        (id) => id !== f.id,
      );
    }
  }

  toggleFollowSelf(): void {
    if (!this.isEditing || !this.taskId) return;
    const meId = this.authService.getCurrentUser()?.id;
    if (!meId) return;
    this.form.markAsDirty();
    if (this.isFollowingSelf) {
      this.subApi
        .unfollowSelf(this.taskId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isFollowingSelf = false;
            this.selectedFollowers = this.selectedFollowers.filter(
              (f) => f.id !== meId,
            );
          },
          error: () =>
            this.confirmDialog.alert({
              title: 'Error',
              message: 'No se pudo dejar de seguir la tarea.',
              variant: 'danger',
            }),
        });
    } else {
      this.subApi
        .followSelf(this.taskId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (resp) => {
            this.isFollowingSelf = true;
            if (!this.selectedFollowers.some((f) => f.id === meId)) {
              this.selectedFollowers.push({
                id: resp.userId,
                label: this.formatUserName(
                  resp.userFullname,
                  resp.userLastname,
                ),
                sublabel: resp.userMail,
              });
            }
          },
          error: () =>
            this.confirmDialog.alert({
              title: 'Error',
              message: 'No se pudo seguir la tarea.',
              variant: 'danger',
            }),
        });
    }
  }

  // ====================================================================
  // "+ Nuevo" — abre cada drawer
  // ====================================================================

  abrirNuevoContacto(): void {
    this.contactoDrawerService.open();
  }
  abrirNuevaEmpresa(): void {
    this.empresaDrawerService.open();
  }
  abrirNuevoDeal(): void {
    this.dealDrawerService.open({
      companyId: this.companySelected?.id,
      companyName: this.companySelected?.label,
      leadId: this.leadSelected?.id,
      leadName: this.leadSelected?.label,
    });
  }
  abrirNuevoLead(): void {
    this.leadDrawerService.open({
      companyId: this.companySelected?.id,
      companyName: this.companySelected?.label,
    });
  }
  abrirNuevaTareaPadre(): void {
    this.taskDrawerService.open();
  }

  // ====================================================================
  // Search functions (devuelven PickerSearchPage paginado)
  // ====================================================================

  private businessId(): number {
    return this.authService.getCurrentUser()?.businessId ?? 1;
  }

  private formatUserName(
    fullname?: string | null,
    lastname?: string | null,
  ): string {
    return `${fullname ?? ''}${lastname ? ' ' + lastname : ''}`.trim() || '—';
  }

  private searchCompaniesFn(
    term: string,
    page: number,
  ): Observable<PickerSearchPage> {
    return this.empresaApi
      .searchCompanies({
        businessId: this.businessId(),
        searchTerm: term || undefined,
        page,
        size: 10,
      })
      .pipe(
        map(
          (res) =>
            ({
              items: (res.content || []).map(
                (c) =>
                  ({
                    id: c.id!,
                    label: c.commercialName,
                    sublabel: c.businessName || undefined,
                  }) as PickerOption,
              ),
              totalPages: res.totalPages,
            }) as PickerSearchPage,
        ),
        catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
      );
  }

  private searchDealsFn(
    term: string,
    page: number,
  ): Observable<PickerSearchPage> {
    return this.dealApi
      .searchDeals({
        businessId: this.businessId(),
        searchName: term || undefined,
        page,
        size: 10,
      })
      .pipe(
        map(
          (res) =>
            ({
              items: (res.content || []).map(
                (d) =>
                  ({
                    id: d.id!,
                    label: d.name,
                    sublabel: d.dealCycleName || d.companyName || undefined,
                  }) as PickerOption,
              ),
              totalPages: res.totalPages,
            }) as PickerSearchPage,
        ),
        catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
      );
  }

  private searchLeadsFn(
    term: string,
    page: number,
  ): Observable<PickerSearchPage> {
    return this.leadApi
      .searchLeads({
        businessId: this.businessId(),
        searchName: term || undefined,
        page,
        size: 10,
      })
      .pipe(
        map(
          (res) =>
            ({
              items: (res.content || []).map(
                (l) =>
                  ({
                    id: l.id!,
                    label: l.name,
                    sublabel:
                      this.formatUserName(
                        l.contactFullname,
                        l.contactLastname,
                      ) || undefined,
                  }) as PickerOption,
              ),
              totalPages: res.totalPages,
            }) as PickerSearchPage,
        ),
        catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
      );
  }

  /** Picker de seguidores — mismo directorio unificado, también con búsqueda en servidor. */
  private searchUsersFn(
    term: string,
    _page: number,
  ): Observable<PickerSearchPage> {
    return this.userDirectory.searchUsers(term || '', 20).pipe(
      map(
        (list) =>
          ({
            items: list.map(
              (u) =>
                ({
                  id: u.id,
                  label: directoryUserLabel(u),
                  sublabel: u.mail,
                }) as PickerOption,
            ),
            totalPages: 1,
          }) as PickerSearchPage,
      ),
      catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
    );
  }

  /**
   * Picker del responsable — directorio unificado (`GET /api/directory/users?query=&limit=20`).
   *
   * La búsqueda es server-side: la pantalla no arrastra todos los usuarios del negocio para
   * filtrarlos en memoria, y el endpoint no exige permiso sobre ningún módulo.
   */
  private searchAssignableUsersFn(
    term: string,
    _page: number,
  ): Observable<PickerSearchPage> {
    return this.userDirectory.searchUsers(term || '', 20).pipe(
      map(
        (list) =>
          ({
            items: list.map((u) => {
              const sublabel = u.roleName
                ? `${u.roleName}${u.mail ? ' · ' + u.mail : ''}`
                : u.mail || undefined;
              return {
                id: u.id,
                label: directoryUserLabel(u),
                sublabel,
              } as PickerOption;
            }),
            totalPages: 1,
          }) as PickerSearchPage,
      ),
      catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
    );
  }

  private searchContactsFn(
    term: string,
    page: number,
  ): Observable<PickerSearchPage> {
    return this.contactoApi
      .searchContacts({
        businessId: this.businessId(),
        searchTerm: term || undefined,
        page,
        size: 10,
      })
      .pipe(
        map(
          (res) =>
            ({
              items: (res.content || []).map(
                (c) =>
                  ({
                    id: c.id!,
                    label: this.formatUserName(c.fullname, c.lastname),
                    sublabel: c.mail,
                  }) as PickerOption,
              ),
              totalPages: res.totalPages,
            }) as PickerSearchPage,
        ),
        catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
      );
  }

  private searchParentTaskFn(
    term: string,
    page: number,
  ): Observable<PickerSearchPage> {
    return this.taskApi
      .filterTasks({
        page,
        size: 10,
        searchTerm: term || undefined,
        isCompleted: false,
      })
      .pipe(
        map(
          (res) =>
            ({
              items: (res.content || [])
                .filter((t) => t.id !== this.taskId)
                .map(
                  (t) =>
                    ({
                      id: t.id,
                      label: t.title,
                      sublabel: t.status?.label,
                    }) as PickerOption,
                ),
              totalPages: res.totalPages,
            }) as PickerSearchPage,
        ),
        catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
      );
  }

  private searchBlockerFn(
    term: string,
    page: number,
  ): Observable<PickerSearchPage> {
    // Excluye la tarea actual y las que ya están seleccionadas/agregadas como bloqueantes.
    const excluded = new Set<number>(this.blockerIdsInUse());
    return this.taskApi
      .filterTasks({ page, size: 10, searchTerm: term || undefined })
      .pipe(
        map(
          (res) =>
            ({
              items: (res.content || [])
                .filter((t) => t.id !== this.taskId && !excluded.has(t.id))
                .map(
                  (t) =>
                    ({
                      id: t.id,
                      label: t.title,
                      sublabel: t.status?.label,
                    }) as PickerOption,
                ),
              totalPages: res.totalPages,
            }) as PickerSearchPage,
        ),
        catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
      );
  }

  // ====================================================================
  // INC 13 — Dependencias (tareas bloqueantes) desde el form
  // ====================================================================

  /** IDs ya usados como bloqueantes (existentes en EDIT + pendientes en CREATE). */
  private blockerIdsInUse(): number[] {
    return [
      ...this.dependencies.map((d) => d.idTaskBlockedBy),
      ...this.pendingBlockerChips.map((c) => c.id),
    ];
  }

  /** Carga las dependencias existentes (solo aplica en EDIT). */
  private loadDependencies(taskId: number): void {
    this.subApi
      .listDependencies(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (deps) => (this.dependencies = deps || []),
        error: () => (this.dependencies = []),
      });
  }

  onBlockerChange(opt: PickerOption | null): void {
    this.newBlockedBy = opt;
  }

  /** Agrega la tarea bloqueante seleccionada. EDIT → POST directo; CREATE → en memoria. */
  agregarDependencia(): void {
    if (!this.newBlockedBy) return;
    const blocker = this.newBlockedBy;
    this.form.markAsDirty();
    if (this.isEditing && this.taskId != null) {
      this.addingDependency = true;
      this.subApi
        .addDependency(this.taskId, { idTaskBlockedBy: blocker.id })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.addingDependency = false;
            this.newBlockedBy = null;
            this.loadDependencies(this.taskId!);
            this.toast.created('Dependencia');
          },
          // 400 auto-dependencia, 404 no existe, 409 ya existe / ciclo → mensaje del backend.
          error: (err) => {
            this.addingDependency = false;
            const msg = this.taskErrorService.getFriendlyMessage(
              err,
              'No se pudo agregar la dependencia.',
            );
            this.toast.error({ title: 'Error', message: msg });
          },
        });
    } else {
      // CREATE: acumular en memoria (evitar duplicados y auto-dependencia básica).
      if (!this.pendingBlockerChips.some((c) => c.id === blocker.id)) {
        this.pendingBlockerChips.push({
          id: blocker.id,
          label: blocker.label,
          sublabel: blocker.sublabel,
        });
      }
      this.newBlockedBy = null;
    }
  }

  /** Quita una dependencia existente (EDIT) con confirmación. */
  async quitarDependencia(d: TaskDependencyResponse): Promise<void> {
    if (this.taskId == null) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Quitar dependencia',
      message: d.blockedByTaskTitle
        ? `¿Quitar "${d.blockedByTaskTitle}" como tarea bloqueante?`
        : '¿Quitar esta tarea bloqueante?',
      confirmText: 'Quitar',
    });
    if (!ok) return;
    this.form.markAsDirty();
    this.subApi
      .removeDependency(this.taskId, d.idTaskBlockedBy)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadDependencies(this.taskId!);
          this.toast.deleted('Dependencia');
        },
        error: (err) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo quitar la dependencia.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  /** Quita una dependencia pendiente (CREATE, solo memoria). */
  quitarDependenciaPendiente(c: ChipItem): void {
    this.pendingBlockerChips = this.pendingBlockerChips.filter(
      (x) => x.id !== c.id,
    );
    this.form.markAsDirty();
  }

  // ===== Helpers =====

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  private atHour(plusDays: number, hour: number): Date {
    const d = new Date();
    d.setDate(d.getDate() + plusDays);
    d.setHours(hour, 0, 0, 0);
    if (plusDays === 0 && d.getTime() <= Date.now()) {
      d.setTime(Date.now() + 60 * 60 * 1000);
      d.setMinutes(0, 0, 0);
    }
    return d;
  }
  private nextMondayAt(hour: number): Date {
    const d = new Date();
    const day = d.getDay();
    const offset = (1 - day + 7) % 7 || 7;
    d.setDate(d.getDate() + offset);
    d.setHours(hour, 0, 0, 0);
    return d;
  }
  private toLocalInput(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return this.toLocalInputFromDate(d);
  }
  private toLocalInputFromDate(d: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  private toIsoFromInput(local: string | null | undefined): string | undefined {
    if (!local) return undefined;
    const d = new Date(local);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  }

  // ===== Save / cancel =====

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.form.pristine) {
      this.toast.info({
        title: 'Tareas',
        message: 'No se realizaron nuevos cambios en la tarea',
      });
      return;
    }

    this.loading = true;
    this.error = null;

    const v = this.form.value;
    const body: TaskRequest = {
      title: v.title!.trim(),
      description: v.description || undefined,
      idTaskType: v.idTaskType ?? undefined,
      idTaskStatus: v.idTaskStatus,
      idTaskPriority: v.idTaskPriority ?? undefined,
      idTaskCategory: v.idTaskCategory ?? undefined,
      datetimeDue: this.toIsoFromInput(v.datetimeDue),
      estimatedDurationMin: v.estimatedDurationMin ?? undefined,
      intIdUserOwner: v.intIdUserOwner ?? undefined,
      intIdCompany: v.intIdCompany ?? undefined,
      intIdDeal: v.intIdDeal ?? undefined,
      intIdLead: v.intIdLead ?? undefined,
      idParentTask: v.idParentTask ?? undefined,
      autoCompleteWhenSubtasksDone: !!v.autoCompleteWhenSubtasksDone,
    };

    if (this.isEditing && this.taskId != null) {
      const update: TaskUpdateRequest = { ...body };
      this.taskApi
        .updateTask(this.taskId, update)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (t) => {
            this.toast.updated('Tarea');
            this.afterSave(t);
          },
          error: (e) => this.handleError(e),
        });
    } else {
      this.taskApi
        .createTask(body)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (created) => {
            this.toast.created('Tarea');
            this.persistSubResources(created.id, created);
          },
          error: (e) => this.handleError(e),
        });
    }
  }

  private persistSubResources(taskId: number, created: TaskResponse): void {
    const calls: Observable<unknown>[] = [];
    for (const cid of this.pendingAddContacts) {
      calls.push(
        this.subApi
          .addContact(taskId, { contactId: cid })
          .pipe(catchError(() => of(null))),
      );
    }
    for (const uid of this.pendingAddFollowers) {
      calls.push(
        this.subApi
          .addFollower(taskId, { userId: uid })
          .pipe(catchError(() => of(null))),
      );
    }
    // INC 13 — crear las dependencias seleccionadas en memoria durante CREATE.
    for (const blocker of this.pendingBlockerChips) {
      calls.push(
        this.subApi
          .addDependency(taskId, { idTaskBlockedBy: blocker.id })
          .pipe(catchError(() => of(null))),
      );
    }
    if (calls.length === 0) {
      this.afterSave(created);
      return;
    }
    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.afterSave(created),
        error: () => this.afterSave(created),
      });
  }

  private afterSave(t: TaskResponse): void {
    this.loading = false;
    if (this.drawerMode) this.saved.emit(t);
    else this.router.navigate(['/tareas']);
  }
  private handleError(e: unknown): void {
    this.loading = false;
    this.error = this.taskErrorService.getFriendlyMessage(
      e,
      'Ocurrió un error al guardar la tarea.',
    );
    this.toast.error({ title: 'Error', message: this.error });
  }
  cancelar(): void {
    if (this.drawerMode) this.cancelled.emit();
    else this.router.navigate(['/tareas']);
  }
}
